"use client"
import { useState } from "react";
import { Breadcrumb } from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useProfils } from "../../hooks/api/profil";
import { ProfilDetail } from "./profil-detail";
import { ProfilForm } from "./profil-form";
import { ProfilList } from "./profil-list-new";

enum View {
  LIST = "list",
  DETAIL = "detail",
  CREATE = "create",
  EDIT = "edit",
}

export function ProfilManagement() {
  const [currentView, setCurrentView] = useState<View>(View.LIST);
  const [selectedProfilId, setSelectedProfilId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: profilsData, isLoading, error, refetch } = useProfils({
    page,
    limit,
    name: searchTerm || undefined,
  });

  const handleCreateClick = () => {
    setCurrentView(View.CREATE);
    setSelectedProfilId(null);
  };

  const handleEditClick = (id: number) => {
    setSelectedProfilId(id);
    setCurrentView(View.EDIT);
  };

  const handleViewClick = (id: number) => {
    setSelectedProfilId(id);
    setCurrentView(View.DETAIL);
  };

  const handleBackToList = () => {
    setCurrentView(View.LIST);
    setSelectedProfilId(null);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1); // Reset to first page when searching
    refetch();
  };

  const onPageChange = (page: number) => {
    setPage(page);
    refetch();
  };

  const onPageSizeChange = (size: number) => {
    setLimit(size);
    setPage(1);
    refetch();
  };

  const renderView = () => {
    switch (currentView) {
      case View.CREATE:
        return <ProfilForm onCancel={handleBackToList} onSuccess={handleBackToList} />;

      case View.EDIT:
        return selectedProfilId ? (
          <ProfilForm
            key={`edit-profil-${selectedProfilId}`}
            profilId={selectedProfilId}
            onCancel={handleBackToList}
            onSuccess={handleBackToList}
          />
        ) : null;

      case View.DETAIL:
        return selectedProfilId ? (
          <ProfilDetail
            profilId={selectedProfilId}
            onBack={handleBackToList}
            onEdit={() => handleEditClick(selectedProfilId)}
          />
        ) : null;

      case View.LIST:
      default:
        return (
          <ProfilList
            profils={profilsData?.data || []}
            isLoading={isLoading}
            error={error}
            onView={handleViewClick}
            onEdit={handleEditClick}
            page={page}
            limit={limit}
            total={profilsData?.total || 0}
            onPageChange={onPageChange}
            onLimitChange={onPageSizeChange}
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="mb-2">
        <Breadcrumb
          items={[
            {
              label: "Gestion",
              onClick: () => {
                if (currentView !== View.LIST) {
                  handleBackToList();
                }
              }
            },
            {
              label: "Profils",
              onClick: () => {
                if (currentView !== View.LIST) {
                  handleBackToList();
                }
              }
            },
            ...(currentView === View.DETAIL && selectedProfilId ?
              [{ label: "Détail du profil", current: true }] :
              []
            ),
            ...(currentView === View.CREATE ?
              [{ label: "Créer un profil", current: true }] :
              []
            ),
            ...(currentView === View.EDIT && selectedProfilId ?
              [{ label: "Modifier le profil", current: true }] :
              []
            ),
          ]}
          showHomeIcon={true}
          onHomeClick={() => {
            if (currentView !== View.LIST) {
              handleBackToList();
            }
          }}
          className="mb-4"
        />
      </div>

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {currentView === View.LIST && "Gestion des Profils"}
          {currentView === View.DETAIL && "Détail du Profil"}
          {currentView === View.CREATE && "Créer un Profil"}
          {currentView === View.EDIT && "Modifier le Profil"}
        </h1>
        {currentView === View.LIST && (
          <Button onClick={handleCreateClick} className="bg-emerald-600 hover:bg-emerald-700">
            Créer un profil
          </Button>
        )}
      </div>

      <Card className="p-4">
        {renderView()}
      </Card>
    </div>
  );
}
